import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, ScanLine, RefreshCw, ArrowRight, Camera, ShieldCheck, AlertTriangle, X, Mail, MessageCircle, CalendarCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { waLink } from "@/constants/site";

const RISK_COLORS = { Low: "#10B981", Medium: "#C9A45C", High: "#EF4444" };
const STEPS = ["Upload photo", "AI identification", "Compatible service", "Estimated price", "Book / Quote"];

const estText = (est) => {
  if (!est) return "Professional assessment recommended";
  if (est.method === "custom" || est.high == null) return est.label || "Custom quote after assessment";
  return `₹${Number(est.low).toLocaleString("en-IN")} – ₹${Number(est.high).toLocaleString("en-IN")}`;
};

export default function AIScan() {
  const [photos, setPhotos] = useState([]); // { preview, base64 }
  const [scanning, setScanning] = useState(false);
  const [report, setReport] = useState(null);
  const [scanId, setScanId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const addFiles = (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) { toast.error("Please choose an image file."); return; }
    list.forEach((file) => {
      if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name} is too large (max 8MB).`); return; }
      const reader = new FileReader();
      reader.onload = (e) => setPhotos((p) => [...p, { preview: e.target.result, base64: e.target.result }]);
      reader.readAsDataURL(file);
    });
    setReport(null);
    track("photo_uploaded", { count: list.length });
  };

  const runScan = async () => {
    if (!photos.length) return;
    setScanning(true);
    track("scan_started", {});
    try {
      const { data } = await api.post("/scan", { image_base64: photos[0].base64 });
      setReport(data.report);
      setScanId(data.id);
      track("scan_completed", { material: data.report?.material });
      if (data.report?.assessment_recommended) track("assessment_recommended", {});
      else track("recommendation_generated", { service: data.report?.recommendation?.service_name });
    } catch (_) {
      toast.error("Unable to analyse this image. Please try a clearer photo.");
    } finally {
      setScanning(false);
    }
  };

  const reset = () => { setPhotos([]); setReport(null); setScanId(null); };

  const rec = report?.recommendation;
  const est = rec?.estimate;
  const assessment = report?.assessment_recommended;

  const waMessage = () => {
    if (assessment) {
      return `Hi ERRAH, I completed the AI Surface Scan (${scanId}) for my ${report?.surface || "surface"}. A professional assessment was recommended — I'd like to book a site inspection.`;
    }
    return `Hi ERRAH, I completed the AI Surface Scan (${scanId}). It identified my surface as ${report?.surface || report?.material} and recommended ${rec?.service_name}. Estimated price shown was ${estText(est)}. I would like an exact quotation.`;
  };

  const goBook = () => {
    track("booking_started", { source: "scan" });
    navigate("/booking", { state: {
      service: assessment ? "Site inspection" : rec?.service_name,
      recommended_service: rec?.service_name, estimate: assessment ? null : estText(est),
      surface_type: report?.material, material: report?.material_canonical || report?.material, scan_id: scanId,
    }});
  };

  const goQuote = () => {
    track("quote_started", { source: "scan" });
    navigate("/quote", { state: {
      surface_type: report?.material, preferred_service: rec?.service_name || "",
      recommended_service: rec?.service_name || "Professional assessment", estimate: assessment ? "" : estText(est),
      material: report?.material_canonical || report?.material, source: "AI Scan",
      scan: { id: scanId, surface: report?.surface, recommended_service: rec?.service_name },
    }});
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14" data-testid="ai-scan-page">
      <Seo title="AI Surface Scan" description="Upload a photo of your surface. ERRAH AI identifies it and recommends the right protection, with an instant estimate." />
      <p className="errah-label gold-text mb-4">AI Surface Scan</p>
      <h1 className="serif-head text-4xl lg:text-6xl max-w-3xl">Show us what you want to protect.</h1>
      <p className="mt-5 text-gray-400 max-w-2xl leading-relaxed">
        ERRAH AI analyses your surface, identifies the likely material and condition, recommends the appropriate protection and gives you an estimated investment.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-gray-500">
        {STEPS.map((s, i) => (
          <span key={s} className="flex items-center gap-3">
            <span className="text-gray-300">{s}</span>
            {i < STEPS.length - 1 && <span className="text-[#C9A45C]">→</span>}
          </span>
        ))}
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start">
        {/* Scanner */}
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            className={`relative aspect-[4/3] hair-border bg-white/[0.02] flex items-center justify-center overflow-hidden transition-colors ${dragOver ? "border-[#C9A45C] bg-white/[0.05]" : ""}`}
            data-testid="scan-dropzone"
          >
            {photos.length ? (
              <>
                <img src={photos[0].preview} alt="Surface preview" className="w-full h-full object-cover" />
                {scanning && <div className="scan-line" />}
                {scanning && <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <p className="glass px-4 py-2 text-sm tracking-widest">ANALYSING SURFACE…</p>
                </div>}
              </>
            ) : (
              <div className="text-center px-6">
                <Upload className="mx-auto text-gray-600" size={40} />
                <p className="mt-4 text-sm text-gray-400">Drag & drop a photo here</p>
                <p className="text-xs text-gray-600 mt-1">or use the buttons below · you can add multiple photos</p>
              </div>
            )}
          </div>

          {photos.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="scan-thumbs">
              {photos.map((p, i) => (
                <div key={i} className="relative w-16 h-16 hair-border overflow-hidden">
                  <img src={p.preview} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/70 p-0.5"><X size={11} /></button>
                </div>
              ))}
            </div>
          )}

          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="scan-file-input"
            onChange={(e) => addFiles(e.target.files)} />
          <input type="file" accept="image/*" capture="environment" className="hidden" id="camera-input"
            onChange={(e) => addFiles(e.target.files)} />

          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={() => inputRef.current?.click()} data-testid="scan-upload-btn"
              className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-sm hover:bg-white hover:text-black transition-colors">
              <Upload size={16} /> Upload photo
            </button>
            <label htmlFor="camera-input" className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-sm hover:bg-white hover:text-black transition-colors cursor-pointer">
              <Camera size={16} /> Take photo
            </label>
            {photos.length > 0 && !scanning && (
              <button onClick={runScan} data-testid="scan-run-btn"
                className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-6 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors">
                <ScanLine size={16} /> Analyse Surface
              </button>
            )}
            {photos.length > 0 && (
              <button onClick={reset} data-testid="scan-reset-btn" className="inline-flex items-center gap-2 text-sm text-gray-400 px-3 py-3 hover:text-white transition-colors">
                <RefreshCw size={16} /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Report */}
        <div data-testid="scan-report-area">
          {!report && !scanning && (
            <div className="hair-border bg-white/[0.02] p-8 h-full flex flex-col justify-center text-center min-h-[320px]">
              <ScanLine className="mx-auto text-gray-700" size={40} />
              <p className="mt-4 text-gray-500 text-sm">Your ERRAH AI assessment will appear here.</p>
            </div>
          )}

          {report && (
            <div className="fade-up space-y-5" data-testid="scan-report">
              {/* Identification */}
              <div className="hair-border bg-white/[0.02] p-6">
                <p className="errah-label gold-text mb-5">AI Surface Assessment</p>
                <div className="grid grid-cols-2 gap-y-5 gap-x-4">
                  <Field label="Detected surface" value={report.surface} big testid="scan-surface" />
                  <Field label="Material" value={report.material} testid="scan-material" />
                  {report.application && <Field label="Application" value={report.application} />}
                  {report.usage && <Field label="Usage" value={report.usage} />}
                  {report.condition && <Field label="Condition" value={report.condition} testid="scan-condition" />}
                  {report.risk && (
                    <div>
                      <p className="errah-label mb-1">Risk</p>
                      <span className="inline-block px-3 py-1 text-sm font-semibold" style={{ color: RISK_COLORS[report.risk] || "#C9A45C", border: `1px solid ${RISK_COLORS[report.risk] || "#C9A45C"}` }}>
                        {report.risk}
                      </span>
                    </div>
                  )}
                </div>
                {report.protection_needs?.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-white/10">
                    <p className="errah-label mb-2">What you need protection from</p>
                    <div className="flex flex-wrap gap-2">
                      {report.protection_needs.map((n) => (
                        <span key={n} className="text-xs px-3 py-1 border border-[#C9A45C]/40 text-[#E4C987] rounded-full">{n}</span>
                      ))}
                    </div>
                  </div>
                )}
                {report.observations?.length > 0 && (
                  <div className="mt-5">
                    <p className="errah-label mb-2">Observations</p>
                    <ul className="space-y-1.5">
                      {report.observations.map((o, i) => (
                        <li key={i} className="text-sm text-gray-400 flex gap-2"><span className="text-[#C9A45C]">•</span>{o}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Recommendation */}
              {!assessment && rec && (
                <div className="hair-border bg-gradient-to-b from-[#C9A45C]/[0.08] to-transparent p-6" data-testid="scan-recommendation">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="text-[#C9A45C]" size={18} />
                    <p className="errah-label gold-text">ERRAH Recommends</p>
                  </div>
                  <p className="font-display text-2xl text-white" data-testid="scan-rec-name">{rec.service_name}</p>
                  <p className="mt-3 text-sm text-gray-300 leading-relaxed">{rec.why}</p>

                  <div className="mt-5 pt-5 border-t border-white/10">
                    <p className="errah-label mb-1">Estimated investment</p>
                    <p className="font-display text-3xl gold-text" data-testid="scan-estimate">{estText(est)}</p>
                    <p className="text-[11px] text-gray-500 mt-1">AI-generated estimate — not a final quotation.</p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-400">
                    {rec.warranty && <span>Warranty: <span className="text-gray-200">{rec.warranty}</span></span>}
                    {rec.site_inspection_required && <span className="text-[#E4C987]">Site inspection recommended</span>}
                    {report.alternative?.service_name && <span>Alternative: <span className="text-gray-200">{report.alternative.service_name}</span></span>}
                  </div>
                </div>
              )}

              {/* Assessment fallback */}
              {assessment && (
                <div className="hair-border bg-white/[0.02] p-6" data-testid="scan-assessment">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="text-[#E4C987]" size={18} />
                    <p className="errah-label gold-text">Professional assessment recommended</p>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {report.assessment_reason || "We could not confidently match a suitable protection for this surface. A professional assessment is recommended for the right solution."}
                  </p>
                </div>
              )}

              {/* CTAs */}
              <div className="grid sm:grid-cols-2 gap-3" data-testid="scan-cta-group">
                <button onClick={goBook} data-testid="scan-book-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black px-5 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">
                  <CalendarCheck size={16} /> {assessment ? "Book Site Inspection" : "Book Installation"}
                </button>
                <button onClick={goQuote} data-testid="scan-quote-cta"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
                  <FileText size={16} /> Request Exact Quote
                </button>
                <a href={waLink(waMessage())} target="_blank" rel="noopener noreferrer"
                  onClick={() => track("whatsapp_click", { location: "scan" })} data-testid="scan-whatsapp-cta"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
                  <MessageCircle size={16} /> WhatsApp ERRAH
                </a>
                <button onClick={() => setEmailOpen(true)} data-testid="scan-email-cta"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
                  <Mail size={16} /> Email Details
                </button>
              </div>
              <p className="text-[11px] text-gray-600 leading-relaxed">
                This is an AI-assisted recommendation, not a guaranteed technical diagnosis. Our team confirms the right solution and final pricing before installation.
              </p>
            </div>
          )}
        </div>
      </div>

      {emailOpen && <EmailModal scanId={scanId} onClose={() => setEmailOpen(false)} />}
    </div>
  );
}

function Field({ label, value, big, testid }) {
  if (!value) return null;
  return (
    <div>
      <p className="errah-label mb-1">{label}</p>
      <p className={big ? "font-display text-2xl" : "text-base text-gray-200"} data-testid={testid}>{value}</p>
    </div>
  );
}

function EmailModal({ scanId, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!email) { toast.error("Please enter your email"); return; }
    setBusy(true);
    try {
      await api.post("/scans/email", { scan_id: scanId, email, name });
      track("scan_email_sent", {});
      toast.success("Your scan details are on the way to your inbox.");
      onClose();
    } catch (_) { toast.error("Could not send. Please check your email and try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-5" onClick={onClose} data-testid="scan-email-modal">
      <div className="hair-border bg-[#111113] p-7 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <p className="errah-label gold-text">Email me my scan details</p>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        <div className="space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="scan-email-name"
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Your email" data-testid="scan-email-input"
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          <button onClick={send} disabled={busy} data-testid="scan-email-send"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60">
            {busy ? "Sending…" : <>Send details <ArrowRight size={16} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
